import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Slider,
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  TrendingUp as StrengthIcon,
  TrendingDown as WeaknessIcon,
  Lightbulb as OpportunityIcon,
  Warning as ThreatIcon,
  Public as EnvironmentIcon,
  ArrowUpward,
  ArrowDownward,
  TrendingFlat
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import useRoadmapStore from '../store/roadmapStore';
import PageContainer from '../components/PageContainer';
import StepPageHeader from '../components/StepPageHeader';
import { STEP_LABELS } from '../utils/constants';
import useAISuggest from '../hooks/useAISuggest';
import apiService from '../utils/api';

const SWOTSection = ({ title, description, items, onAdd, onDelete, onUpdate, icon, color, placeholder, enableScoring = false }) => {
  const [newItem, setNewItem] = useState({ text: '', impact: 3, urgency: 3, trend: 'stable' });
  const { t } = useTranslation();

  const handleAdd = () => {
    if (newItem.text.trim()) {
      const itemToAdd = enableScoring 
        ? { text: newItem.text.trim(), impact: newItem.impact, urgency: newItem.urgency, trend: newItem.trend }
        : newItem.text.trim();
      onAdd(itemToAdd);
      setNewItem({ text: '', impact: 3, urgency: 3, trend: 'stable' });
    }
  };

  const getPriorityColor = (impact, urgency) => {
    const score = impact * urgency;
    if (score >= 20) return 'error.main';
    if (score >= 12) return 'warning.main';
    return 'success.main';
  };

  const getTrendIcon = (trend) => {
    switch(trend) {
      case 'increasing': return <ArrowUpward fontSize="small" />;
      case 'decreasing': return <ArrowDownward fontSize="small" />;
      default: return <TrendingFlat fontSize="small" />;
    }
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader
        title={title}
        subheader={description}
        avatar={React.cloneElement(icon, { sx: { color } })}
        titleTypographyProps={{ variant: 'h6' }}
        subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
      />
      <CardContent>
        <Stack spacing={2}>
          <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                size="small"
                placeholder={placeholder}
                value={newItem.text}
                onChange={(e) => setNewItem({ ...newItem, text: e.target.value })}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !enableScoring) {
                    handleAdd();
                  }
                }}
              />
              {enableScoring && (
                <>
                  <Box>
                    <Typography variant="caption" gutterBottom>
                      Impact: {newItem.impact}/5
                    </Typography>
                    <Slider
                      value={newItem.impact}
                      onChange={(e, val) => setNewItem({ ...newItem, impact: val })}
                      min={1}
                      max={5}
                      step={1}
                      marks
                      valueLabelDisplay="auto"
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" gutterBottom>
                      Urgency: {newItem.urgency}/5
                    </Typography>
                    <Slider
                      value={newItem.urgency}
                      onChange={(e, val) => setNewItem({ ...newItem, urgency: val })}
                      min={1}
                      max={5}
                      step={1}
                      marks
                      valueLabelDisplay="auto"
                      size="small"
                    />
                  </Box>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Trend</InputLabel>
                    <Select
                      value={newItem.trend}
                      onChange={(e) => setNewItem({ ...newItem, trend: e.target.value })}
                      label="Trend"
                    >
                      <MenuItem value="increasing">Increasing</MenuItem>
                      <MenuItem value="stable">Stable</MenuItem>
                      <MenuItem value="decreasing">Decreasing</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={handleAdd}
                disabled={!newItem.text.trim()}
                startIcon={<AddIcon />}
              >
                {t('common.add')}
              </Button>
            </Stack>
          </Card>
          
          <Stack gap={1} minHeight={140}>
            {items.map((item, index) => {
              const isObject = typeof item === 'object';
              const text = isObject ? item.text : item;
              const impact = isObject ? item.impact : null;
              const urgency = isObject ? item.urgency : null;
              const trend = isObject ? item.trend : null;
              const priorityScore = impact && urgency ? impact * urgency : null;

              return (
                <Card key={index} variant="outlined" sx={{ 
                  borderLeft: enableScoring && priorityScore ? `4px solid` : 'none',
                  borderLeftColor: enableScoring ? getPriorityColor(impact, urgency) : 'transparent'
                }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2">{text}</Typography>
                        {enableScoring && (
                          <Stack direction="row" spacing={1} mt={0.5}>
                            <Tooltip title="Impact">
                              <Chip label={`I:${impact}`} size="small" />
                            </Tooltip>
                            <Tooltip title="Urgency">
                              <Chip label={`U:${urgency}`} size="small" />
                            </Tooltip>
                            <Tooltip title={`Priority Score: ${priorityScore}`}>
                              <Chip 
                                label={`P:${priorityScore}`} 
                                size="small" 
                                sx={{ 
                                  bgcolor: getPriorityColor(impact, urgency),
                                  color: 'white'
                                }} 
                              />
                            </Tooltip>
                            <Tooltip title={`Trend: ${trend}`}>
                              <Chip 
                                icon={getTrendIcon(trend)}
                                label={trend} 
                                size="small" 
                                variant="outlined"
                              />
                            </Tooltip>
                          </Stack>
                        )}
                      </Box>
                      <IconButton onClick={() => onDelete(index)} size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
            {items.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                {t('stepContent.situation.empty.swot', { section: title })}
              </Typography>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default function SituationPage() {
  const { t, i18n } = useTranslation();
  const handleAISuggest = useAISuggest('situation_analysis');
  const _storeStep = useRoadmapStore(s => s.roadmapData?.steps?.situation_analysis);
  const { 
    getStepData, 
    updateStepData, 
    markStepComplete, 
    isStepCompleted 
  } = useRoadmapStore();

  const stepData = getStepData('situation_analysis');
  const toArr = v => Array.isArray(v) ? v : [];
  const [strengths, setStrengths] = useState(toArr(stepData.strengths));
  const [weaknesses, setWeaknesses] = useState(toArr(stepData.weaknesses));
  const [opportunities, setOpportunities] = useState(toArr(stepData.opportunities));
  const [threats, setThreats] = useState(toArr(stepData.threats));
  const [environmentalFactors, setEnvironmentalFactors] = useState(toArr(stepData.environmental_factors));
  const [isComplete, setIsComplete] = useState(isStepCompleted('situation_analysis'));
  const [marketScanLoading, setMarketScanLoading] = useState(false);
  const [marketScanResult, setMarketScanResult] = useState(null);

  const { roadmapData } = useRoadmapStore();

  const handleMarketScan = async () => {
    setMarketScanLoading(true);
    try {
      const desc = roadmapData.steps?.vision?.vision_statement || roadmapData.project_name || '';
      const result = await apiService.marketScan(desc, '', i18n.language);
      setMarketScanResult(result);
      // Auto-apply opportunities and threats
      if (result.opportunities) {
        const newOpps = [...opportunities, ...result.opportunities.map(o => ({ text: o.opportunity || o, impact: 3, urgency: 3, trend: 'stable' }))];
        setOpportunities(newOpps);
        updateStepData('situation_analysis', { strengths, weaknesses, opportunities: newOpps, threats, environmental_factors: environmentalFactors });
      }
      if (result.threats) {
        const newThreats = [...threats, ...result.threats.map(th => ({ text: th.threat || th, impact: 4, urgency: 3, trend: 'increasing' }))];
        setThreats(newThreats);
        updateStepData('situation_analysis', { strengths, weaknesses, opportunities, threats: newThreats, environmental_factors: environmentalFactors });
      }
    } catch {
      setMarketScanResult(null);
    } finally {
      setMarketScanLoading(false);
    }
  };

  useEffect(() => {
    const data = getStepData('situation_analysis');
    const toArr = v => Array.isArray(v) ? v : [];
    setStrengths(toArr(data.strengths));
    setWeaknesses(toArr(data.weaknesses));
    setOpportunities(toArr(data.opportunities));
    setThreats(toArr(data.threats));
    setEnvironmentalFactors(toArr(data.environmental_factors));
    setIsComplete(isStepCompleted('situation_analysis'));
  }, [getStepData, isStepCompleted, _storeStep]);

  const handleAddItem = (setter, items) => (newItem) => {
    const updated = [...items, newItem];
    setter(updated);
    updateStepData('situation_analysis', {
      strengths,
      weaknesses,
      opportunities,
      threats,
      environmental_factors: environmentalFactors,
      ...(setter === setStrengths && { strengths: updated }),
      ...(setter === setWeaknesses && { weaknesses: updated }),
      ...(setter === setOpportunities && { opportunities: updated }),
      ...(setter === setThreats && { threats: updated }),
      ...(setter === setEnvironmentalFactors && { environmental_factors: updated })
    });
  };

  const handleDeleteItem = (setter, items) => (index) => {
    const updated = items.filter((_, i) => i !== index);
    setter(updated);
    updateStepData('situation_analysis', {
      strengths,
      weaknesses,
      opportunities,
      threats,
      environmental_factors: environmentalFactors,
      ...(setter === setStrengths && { strengths: updated }),
      ...(setter === setWeaknesses && { weaknesses: updated }),
      ...(setter === setOpportunities && { opportunities: updated }),
      ...(setter === setThreats && { threats: updated }),
      ...(setter === setEnvironmentalFactors && { environmental_factors: updated })
    });
  };

  const handleCompleteToggle = () => {
    const newCompleteState = !isComplete;
    const success = markStepComplete('situation_analysis', newCompleteState);
    setIsComplete(success);
  };

  const canComplete = strengths.length > 0 && weaknesses.length > 0;

  return (
    <PageContainer maxWidth={1300}>
      <Stack spacing={3}>
        <StepPageHeader
          stepNum={2}
          title={t(STEP_LABELS.situation_analysis)}
          intro={t('stepContent.situation.intro')}
          isComplete={isComplete}
          canComplete={canComplete}
          onToggleComplete={handleCompleteToggle}
          requireMessage={t('stepContent.situation.alerts.requireItems')}
        onAISuggest={handleAISuggest}
        />

        {/* Market Scan Button */}
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            onClick={handleMarketScan}
            disabled={marketScanLoading}
            startIcon={marketScanLoading ? <CircularProgress size={14} /> : <EnvironmentIcon />}
            sx={{
              border: '1px solid rgba(16,185,129,0.3)',
              color: 'var(--status-green)',
              background: 'rgba(16,185,129,0.08)',
              '&:hover': { borderColor: '#10b981', background: 'rgba(16,185,129,0.18)' },
              fontWeight: 600, fontSize: '0.78rem', textTransform: 'none', px: 2
            }}
          >
            {marketScanLoading ? t('ai.scanning') : t('ai.marketScan')}
          </Button>
          <Typography variant="caption" sx={{ color: 'var(--fg-faint)', alignSelf: 'center' }}>
            {t('ai.poweredByGrok')}
          </Typography>
        </Stack>

        {marketScanResult && (
          <Alert severity="success" onClose={() => setMarketScanResult(null)} sx={{ fontSize: '0.82rem' }}>
            {t('ai.resultsApplied')}
          </Alert>
        )}

        <Paper elevation={2} sx={{ p: { xs: 2.5, md: 4 } }}>
          <Typography variant="h5" gutterBottom>
            {t('stepContent.situation.sections.swot.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('stepContent.situation.sections.swot.description')}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SWOTSection
                title={t('stepContent.situation.sections.swot.strengths.title')}
                description={t('stepContent.situation.sections.swot.strengths.description')}
                items={strengths}
                onAdd={handleAddItem(setStrengths, strengths)}
                onDelete={handleDeleteItem(setStrengths, strengths)}
                icon={<StrengthIcon />}
                color="success.main"
                placeholder={t('stepContent.situation.sections.swot.strengths.placeholder')}
                enableScoring={true}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <SWOTSection
                title={t('stepContent.situation.sections.swot.weaknesses.title')}
                description={t('stepContent.situation.sections.swot.weaknesses.description')}
                items={weaknesses}
                onAdd={handleAddItem(setWeaknesses, weaknesses)}
                onDelete={handleDeleteItem(setWeaknesses, weaknesses)}
                icon={<WeaknessIcon />}
                color="error.main"
                placeholder={t('stepContent.situation.sections.swot.weaknesses.placeholder')}
                enableScoring={true}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <SWOTSection
                title={t('stepContent.situation.sections.swot.opportunities.title')}
                description={t('stepContent.situation.sections.swot.opportunities.description')}
                items={opportunities}
                onAdd={handleAddItem(setOpportunities, opportunities)}
                onDelete={handleDeleteItem(setOpportunities, opportunities)}
                icon={<OpportunityIcon />}
                color="primary.main"
                placeholder={t('stepContent.situation.sections.swot.opportunities.placeholder')}
                enableScoring={true}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <SWOTSection
                title={t('stepContent.situation.sections.swot.threats.title')}
                description={t('stepContent.situation.sections.swot.threats.description')}
                items={threats}
                onAdd={handleAddItem(setThreats, threats)}
                onDelete={handleDeleteItem(setThreats, threats)}
                icon={<ThreatIcon />}
                color="warning.main"
                placeholder={t('stepContent.situation.sections.swot.threats.placeholder')}
                enableScoring={true}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={2} sx={{ p: { xs: 2.5, md: 4 } }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <EnvironmentIcon />
            <Typography variant="h5">
              {t('stepContent.situation.sections.environmental.title')}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('stepContent.situation.sections.environmental.description')}
          </Typography>
          
          <SWOTSection
            title={t('stepContent.situation.sections.environmental.title')}
            description=""
            items={environmentalFactors}
            onAdd={handleAddItem(setEnvironmentalFactors, environmentalFactors)}
            onDelete={handleDeleteItem(setEnvironmentalFactors, environmentalFactors)}
            icon={<EnvironmentIcon />}
            color="info.main"
            placeholder={t('stepContent.situation.sections.environmental.placeholder')}
          />
        </Paper>
      </Stack>
    </PageContainer>
  );
}
